import React, { useState } from 'react';
import { Badge, Box, Button, CircularProgress, Divider, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {useDispatch, useSelector} from 'react-redux';
import { RootState } from '../../store';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { useTranslation } from 'react-i18next';
import {clearUnreadNotifications} from "../../store/slices/authSlice";

type NotificationType = 'SUCCESS' | 'INFO' | 'WARNING' | 'ERROR';

interface Notification {
  id: number;
  message: string;
  creationDate: string;
  read: boolean;
  redirectTo: string;
  type: NotificationType;
}

const typeBgColorMap: Record<NotificationType, string> = {
  SUCCESS: 'rgba(0, 128, 0, 0.05)',
  INFO: 'rgba(0, 0, 255, 0.05)',
  WARNING: 'rgba(255, 193, 7, 0.05)',
  ERROR: 'rgba(255, 0, 0, 0.05)',
};

const NavBarNotification = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Zugriff auf den globalen State
  const totalUnreadNotifications = useSelector((state: RootState) => state.auth.statistics?.totalUnreadNotifications || 0);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();

  const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setIsLoading(true);
    await fetchLatestNotifications();
    setIsLoading(false);
  };

  const handleClose = () => setAnchorEl(null);

  const fetchLatestNotifications = async () => {
    try {
      const res = await api.get('user/notifications', {
        params: {
          page: 0,
          size: 5,
          sort: 'creationDate,desc',
        },
      });
      setNotifications(res.data.content);
      dispatch(clearUnreadNotifications());
    } catch (err) {
      console.error('Error fetching notifications', err);
    }
  };

  const handleNotificationClick = async (notif: Notification) => {
    handleClose();
    if (notif.redirectTo) {
      navigate(notif.redirectTo);
    } else {
      navigate('/profile/notifications');
    }
  };

  return (
      <>
        <IconButton
            onClick={handleClick}
            size="medium"
            sx={{
              width: 40,
              height: 40,
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
        >
          <Badge
              badgeContent={totalUnreadNotifications > 99 ? '99+' : totalUnreadNotifications}
              color="error"
              invisible={totalUnreadNotifications === 0}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <Menu
            anchorEl={anchorEl}
            id="notification-menu"
            open={open}
            onClose={handleClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            disableScrollLock={true}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  borderRadius: '8px',
                  border: '1px solid #404040',
                  bgcolor: 'background.paper',
                  '&::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 12,
                    height: 12,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    borderLeft: '1px solid #404040',
                    borderTop: '1px solid #404040',
                  },
                },
              },
            }}
        >
          {isLoading ? (
              <Box className="flex justify-center p-4">
                <CircularProgress size={24} />
              </Box>
          ) : notifications.length > 0 ? (
              notifications.map((notif, idx) => (
                  <React.Fragment key={notif.id}>
                    <MenuItem
                        onClick={() => handleNotificationClick(notif)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: typeBgColorMap[notif.type],
                          p: 1.5,
                        }}
                    >
                      {!notif.read && (
                          <Box
                              sx={{
                                minWidth: 28,
                                height: 20,
                                bgcolor: 'primary.main',
                                color: 'white',
                                fontSize: 10,
                                fontWeight: 'bold',
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 1,
                              }}
                          >
                            {t('profile.notifications.new')}
                          </Box>
                      )}
                      <Typography variant="body2">{notif.message}</Typography>
                    </MenuItem>
                    {idx < notifications.length - 1 && (
                        <Divider sx={{ margin: '0 !important', padding: '0 !important' }} />
                    )}
                  </React.Fragment>
              ))
          ) : (
              <MenuItem disabled>
                {t('profile.notifications.noNotifications')}
              </MenuItem>
          )}

          <div className="flex justify-center">
            <Button
                variant="contained"
                color="primary"
                sx={{ marginY: 1 }}
                onClick={() => {
                  handleClose();
                  navigate('/profile/notifications');
                }}
            >
              {t('profile.notifications.moreNotifications')}
            </Button>
          </div>
        </Menu>
      </>
  );
};

export default NavBarNotification;